/**
 * BlurFade Animation Component
 * 
 * Adapted from Magic UI for Upzora dashboard.
 * Per design.md: 150-300ms entrance transitions (opacity + translateY only)
 * 
 * Uses Framer Motion for smooth, performance-safe animations.
 * Respects prefers-reduced-motion preference.
 */

import { useRef } from 'react';
import {
    AnimatePresence,
    motion,
    useInView,
    type Variants,
} from 'framer-motion';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface BlurFadeProps {
    children: React.ReactNode;
    className?: string;
    duration?: number;
    delay?: number;
    offset?: number;
    direction?: 'up' | 'down';
    inView?: boolean;
}

export function BlurFade({
    children,
    className,
    duration = 0.3, // Per design.md: 150-300ms
    delay = 0,
    offset = 12, // Per design.md: 10-16px slide
    direction = 'up',
    inView = true,
}: BlurFadeProps) {
    const ref = useRef<HTMLDivElement>(null);
    const inViewResult = useInView(ref, { once: true, margin: '-50px' });
    const isInView = !inView || inViewResult;
    const prefersReducedMotion = useReducedMotion();

    // If user prefers reduced motion, render without animation
    if (prefersReducedMotion) {
        return <div className={className}>{children}</div>;
    }

    const variants: Variants = {
        hidden: {
            y: direction === 'up' ? offset : -offset,
            opacity: 0,
            filter: 'blur(4px)',
        },
        visible: {
            y: 0,
            opacity: 1,
            filter: 'blur(0px)',
        },
    };

    return (
        <AnimatePresence>
            <motion.div
                ref={ref}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                exit="hidden"
                variants={variants}
                transition={{
                    delay: 0.04 + delay,
                    duration,
                    ease: 'easeOut',
                }}
                className={className}
            >
                {children}
            </motion.div>
        </AnimatePresence>
    );
}
